<script>
  import { format } from 'svelte-i18n';

  import {
    Field,
    FieldActions,
    FieldError,
    FieldHeader,
    FieldLabel,
    FieldValue
  } from '@client/components/fields/standalone/base';
  import { FolderTree } from '@client/components/folder-tree';

  export let id = null;
  export let label = null;

  export let files = [];

  export let tooltip = undefined;
  export let tooltipIcon = undefined;

  export let readonly = false;
  export let editing = false;

  export let showActions = true;
  export let validationError = undefined;

  export let onUpdate = () => {};

  let disableSubmit = false;
  let items = JSON.parse(JSON.stringify(files));

  let error = null;

  const onEdit = () => {
    editing = true;
  };

  const onCancel = () => {
    editing = false;
    items = JSON.parse(JSON.stringify(files));
    error = null;
  };

  const onSave = () => {
    error = null;
    editing = false;
    files = JSON.parse(JSON.stringify(items));

    if (onUpdate) {
      onUpdate(id, label, items);
    }
  };

  const addFolder = (key, value, newItem) => {
    items.some(function iterate(item, index, array) {
      if (item[key] === value) {
        array[index].files.push(newItem);
        return true;
      }

      return Array.isArray(item.files) && item.files.some(iterate);
    });

    items = [...items];
  };

  const renameFolder = (key, value, updatedName, parent) => {
    const names = items.flatMap(function collectNames(item) {
      if (item[key] !== value && item.parent === parent) {
        return (item?.files || []).map((file) => file.name || null).filter(Boolean);
      }

      return Array.isArray(item.files) ? item.files.flatMap(collectNames) : [];
    });

    items.some(function iterate(item) {
      if (item[key] === value) {
        delete item.renaming;

        if (names.includes(updatedName)) {
          error = true;
          validationError = $format('label.DUPLICATE_FOLDER_NAME', { values: { name: updatedName } });
          return true;
        }

        error = null;
        validationError = null;
        item.name = updatedName;

        return true;
      }

      return Array.isArray(item.files) && item.files.some(iterate);
    });

    items = [...items];
  };

  const deleteFolder = (key, value) => {
    items.some(function iterate(item, index, array) {
      if (item[key] === value) {
        array.splice(index, 1);
        return true;
      }

      return Array.isArray(item.files) && item.files.some(iterate);
    });

    items = [...items];
  };

  const onAdd = ({ id, files, parent }) => {
    const guid = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

    addFolder('id', id, {
      id: guid,
      name: $format('label.NEW_FOLDER'),
      parent: parent,
      renaming: true,
      files: []
    });

    items = [...items];
  };

  const onRename = ({ id, name, parent }) => renameFolder('id', id, name, parent);
  const onDelete = ({ id }) => deleteFolder('id', id);

  $: hasErrors = error?.length;
</script>

<Field>
  <FieldHeader>
    <FieldLabel {label} {tooltipIcon} {tooltip} bind:error={hasErrors} />
    <FieldActions bind:readonly bind:editing bind:disableSubmit {onSave} {onEdit} {onCancel} {showActions} />
  </FieldHeader>
  <FieldValue {showActions}>
    <div class="field-list">
      {#if editing}
        <FolderTree bind:files={items} editable {onAdd} {onDelete} {onRename} expanded />
      {:else}
        <FolderTree bind:files={items} expanded />
      {/if}
    </div>
  </FieldValue>
  <FieldError bind:error {validationError} />
  {#if !editing}
    <slot name="preview" />
  {/if}
</Field>

<style lang="scss" src="./folder-tree.scss"></style>
